import React from "react";
import { Edit, Trash2 } from "lucide-react";
import type { TFunction } from "i18next";
import { Badge, BadgeVariant } from "../../../../components/badge";
import { ButtonIcon } from "../../../../components/button";
import type { DataTableColumn } from "../../../../components/table";
import type {
  DataTableDateRangeValue,
  DataTableNumberRangeValue,
  DataTableSortDirection,
} from "../../../../components/table";
import { SurveyStatus, type SurveyListItemForAdminResponse } from "../../types/survey.types";
import type { SurveyListFilters } from "../../hooks/useGetSurveyList";

type BuildSurveyListColumnsParams = {
  t: TFunction;
  filters: SurveyListFilters;
  updateFilter: <K extends keyof SurveyListFilters>(
    key: K,
    value: SurveyListFilters[K] | null,
  ) => void;
  onEdit: (surveyItem: SurveyListItemForAdminResponse) => void;
  onDelete: (surveyItem: SurveyListItemForAdminResponse) => void;
  getSurveyStatus: (status: SurveyStatus) => string;
  sortDirections: Partial<
    Record<"points" | "question_count" | "responses_count", DataTableSortDirection | null>
  >;
  onColumnSort: (
    columnName: "points" | "question_count" | "responses_count",
    nextDirection: DataTableSortDirection | null,
  ) => void;
};

const formatSurveyDate = (value: string) => {
  const date = new Date(value);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const isEmptyNumberRange = (value: DataTableNumberRangeValue | undefined) =>
  !value || (value.min === null && value.max === null);

const isEmptyDateRange = (value: DataTableDateRangeValue | undefined) =>
  !value || (value.from.trim() === "" && value.to.trim() === "");

const SurveyActionsCell = ({
  surveyItem,
  onEdit,
  onDelete,
  t,
}: {
  surveyItem: SurveyListItemForAdminResponse;
  onEdit: (surveyItem: SurveyListItemForAdminResponse) => void;
  onDelete: (surveyItem: SurveyListItemForAdminResponse) => void;
  t: TFunction;
}) => (
  <div className="flex justify-center gap-1">
    <ButtonIcon
      title={t("common.edit")}
      aria-label={t("common.edit")}
      className="h-8 w-8 rounded-md border border-gray-300 bg-white hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"
      onClick={(e) => {
        e.stopPropagation();
        onEdit(surveyItem);
      }}
    >
      <Edit className="h-4 w-4 text-gray-700 dark:text-gray-300" />
    </ButtonIcon>
    <ButtonIcon
      title={t("common.delete")}
      aria-label={t("common.delete")}
      className="h-8 w-8 rounded-md border border-gray-300 bg-white !text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-gray-600 dark:bg-gray-800 dark:text-red-400 dark:hover:bg-red-950/30"
      onClick={(e) => {
        e.stopPropagation();
        onDelete(surveyItem);
      }}
    >
      <Trash2 className="h-4 w-4" />
    </ButtonIcon>
  </div>
);

export const buildSurveyListColumns = ({
  t,
  filters,
  updateFilter,
  onEdit,
  onDelete,
  getSurveyStatus,
  sortDirections,
  onColumnSort,
}: BuildSurveyListColumnsParams): DataTableColumn<SurveyListItemForAdminResponse>[] => [
  {
    key: "title",
    title: t("common.title"),
    filter: {
      type: "string",
      value: filters.title || "",
      onFilter: (value: string) => updateFilter("title", value.trim() ? value : null),
      placeholder: `Search ${t("common.title").toLowerCase()}...`,
      title: `FILTER BY ${t("common.title").toUpperCase()}`,
      description: "Press Enter to search",
      submitMode: "enter",
    },
    render: (surveyItem) => <span className="font-medium">{surveyItem.title}</span>,
  },
  {
    key: "description",
    title: t("common.description"),
    filter: {
      type: "string",
      value: filters.description || "",
      onFilter: (value: string) => updateFilter("description", value.trim() ? value : null),
      placeholder: `Search ${t("common.description").toLowerCase()}...`,
      title: `FILTER BY ${t("common.description").toUpperCase()}`,
      description: "Press Enter to search",
      submitMode: "enter",
    },
    render: (surveyItem) => (
      <div className="max-w-xs truncate text-gray-900 dark:text-white">
        {surveyItem.description}
      </div>
    ),
  },
  {
    key: "campaign",
    title: "Campaign / Program",
    render: (surveyItem) =>
      surveyItem.campaign ? (
        <div>
          <div>{surveyItem.campaign.name}</div>
          <div className="text-xs text-gray-500">{surveyItem.campaign.programName}</div>
        </div>
      ) : (
        <span className="text-gray-500">—</span>
      ),
  },
  {
    key: "points",
    title: t("survey.points"),
    filter: {
      type: "range_number",
      value: filters.points || { min: null, max: null },
      onFilter: (value: DataTableNumberRangeValue) =>
        updateFilter("points", isEmptyNumberRange(value) ? null : value),
      title: "FILTER BY POINTS RANGE",
      minLabel: "Min Points",
      maxLabel: "Max Points",
      placeholder: "points",
    },
    render: (surveyItem) => <span>{surveyItem.points}</span>,
    align: "center",
    sort: {
      direction: sortDirections.points ?? null,
      onSort: (nextDirection) => onColumnSort("points", nextDirection),
      title: t("survey.points"),
    },
  },
  {
    key: "end_time",
    title: t("survey.end_time"),
    filter: {
      type: "range_date",
      value: filters.end_time || { from: "", to: "" },
      onFilter: (value: DataTableDateRangeValue) =>
        updateFilter("end_time", isEmptyDateRange(value) ? null : value),
      title: "FILTER BY END TIME",
      fromLabel: "From date",
      toLabel: "To date",
    },
    render: (surveyItem) => <span>{formatSurveyDate(surveyItem.end_time)}</span>,
    align: "center",
  },
  {
    key: "status",
    title: t("common.status"),
    filter: {
      type: "dropdown",
      value: filters.status || "",
      onFilter: (value: string) =>
        updateFilter("status", value.trim() ? (value as SurveyListFilters["status"]) : null),
      title: "FILTER BY STATUS",
      placeholder: "All Status",
      clearLabel: "Clear",
      options: [
        { value: "open", label: "Open Surveys" },
        { value: "closed", label: "Closed Surveys" },
        { value: "unpublished", label: "Unpublished" },
      ],
    },
    render: (surveyItem) => {
      let variant: BadgeVariant = "outline";

      if (surveyItem.status === SurveyStatus.Open) {
        variant = "default";
      }
      if (surveyItem.status === SurveyStatus.Unpublished) {
        variant = "secondary";
      }

      return (
        <Badge variant={variant}>
          {surveyItem.status ? getSurveyStatus(surveyItem.status) : "-"}
        </Badge>
      );
    },
    align: "center",
  },
  {
    key: "question_count",
    title: t("survey.questions"),
    render: (surveyItem) => <span>{surveyItem.question_count}</span>,
    align: "center",
    sort: {
      direction: sortDirections.question_count ?? null,
      onSort: (nextDirection) => onColumnSort("question_count", nextDirection),
      title: t("survey.questions"),
    },
  },
  {
    key: "responses_count",
    title: t("survey.responses"),
    render: (surveyItem) => <span>{surveyItem.responses_count}</span>,
    align: "center",
    sort: {
      direction: sortDirections.responses_count ?? null,
      onSort: (nextDirection) => onColumnSort("responses_count", nextDirection),
      title: t("survey.responses"),
    },
  },
  {
    key: "actions",
    title: t("common.actions"),
    render: (surveyItem) => (
      <SurveyActionsCell
        surveyItem={surveyItem}
        onEdit={onEdit}
        onDelete={onDelete}
        t={t}
      />
    ),
    align: "center",
    cellClassName: "w-32",
  },
];
